/**
 * Extração de WABA e phoneNumberId do Endpoint FHIR (contrato CRM).
 *
 * Sempre usa o contato `rank === 1` (número preferencial).
 * Não lê o shape antigo (`contact.telecom` / `whatsapp-business-config`).
 */

/** Extension FHIR do WABA no contato rank 1. */
export const WABA_ID_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/StructureDefinition/waba-id";

/** Extension FHIR do Meta phone-number-id em cada número. */
export const PHONE_NUMBER_ID_EXTENSION_URL =
  "https://fluxmed.com.br/fhir/StructureDefinition/whatsapp-phone-number-id";

export interface WhatsAppEndpointContactExtension {
  url?: string;
  valueString?: string;
}

export interface WhatsAppEndpointContact {
  system?: string;
  value?: string;
  rank?: number;
  extension?: WhatsAppEndpointContactExtension[];
}

export interface WhatsAppEndpointResource {
  resourceType?: string;
  id?: string;
  status?: string;
  contact?: WhatsAppEndpointContact[];
}

function findRankOneContact(
  endpoint: WhatsAppEndpointResource,
): WhatsAppEndpointContact | undefined {
  return (endpoint.contact ?? []).find((contact) => contact.rank === 1);
}

function readExtensionValue(
  contact: WhatsAppEndpointContact,
  extensionUrl: string,
): string | null {
  const match = (contact.extension ?? []).find(
    (extension) => extension.url === extensionUrl,
  );
  const value = match?.valueString?.trim();
  return value ? value : null;
}

/**
 * WABA do número preferencial (contact.rank === 1).
 */
export function selectWabaIdFromEndpoint(
  endpoint: WhatsAppEndpointResource,
): string | null {
  const rankOne = findRankOneContact(endpoint);
  if (!rankOne) return null;
  return readExtensionValue(rankOne, WABA_ID_EXTENSION_URL);
}

/**
 * Meta phone-number-id do número preferencial (contact.rank === 1).
 * É o identificador preferencial no path do FluxZap.
 */
export function selectPhoneNumberIdFromEndpoint(
  endpoint: WhatsAppEndpointResource,
): string | null {
  const rankOne = findRankOneContact(endpoint);
  if (!rankOne) return null;
  return readExtensionValue(rankOne, PHONE_NUMBER_ID_EXTENSION_URL);
}

/**
 * Número visível do contato rank 1 (`value`), não o phoneNumberId da Meta.
 * Usado no cabeçalho e no banner do dialog.
 */
export function selectDisplayPhoneFromEndpoint(
  endpoint: WhatsAppEndpointResource,
): string | null {
  const rankOne = findRankOneContact(endpoint);
  if (!rankOne) return null;
  if ((rankOne.system ?? "phone") !== "phone") return null;
  const display = rankOne.value?.trim();
  return display ? display : null;
}
